import { Workbox } from 'workbox-window';
import { isPaymentPending, queuePwaRefreshWhenIdle } from './paymentPendingGate.js';

export interface PwaUpdateHandlers {
  readonly setUpdateReady: (ready: boolean) => void;
  readonly setApplyUpdate: (apply: (() => void) | null) => void;
}

function waitForServiceWorkerInstalled(worker: ServiceWorker): Promise<void> {
  if (worker.state === 'installed') {
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      worker.removeEventListener('statechange', onStateChange);
      resolve();
    }, 5_000);

    const onStateChange = (): void => {
      if (worker.state === 'installed' || worker.state === 'activated') {
        window.clearTimeout(timeoutId);
        worker.removeEventListener('statechange', onStateChange);
        resolve();
        return;
      }
      if (worker.state === 'redundant') {
        window.clearTimeout(timeoutId);
        worker.removeEventListener('statechange', onStateChange);
        reject(new Error('Service worker became redundant during install'));
      }
    };
    worker.addEventListener('statechange', onStateChange);
  });
}

export interface CustomerServiceWorkerUpdateResult {
  readonly registration: ServiceWorkerRegistration | null;
  readonly hasWaiting: boolean;
}

/**
 * Asks the browser to check for an updated SW (production only).
 * Used by pull-to-refresh before deciding reload vs query refresh.
 */
export async function updateCustomerServiceWorkerRegistration(): Promise<CustomerServiceWorkerUpdateResult> {
  if (!('serviceWorker' in navigator)) {
    return { registration: null, hasWaiting: false };
  }
  if (import.meta.env.DEV) {
    return { registration: null, hasWaiting: false };
  }

  let registration: ServiceWorkerRegistration | undefined;
  try {
    registration = await navigator.serviceWorker.getRegistration();
  } catch (err) {
    throw new Error(
      `Failed to read service worker registration: ${err instanceof Error ? err.message : String(err)}`,
    );
  }

  if (registration === undefined) {
    return { registration: null, hasWaiting: false };
  }

  try {
    await registration.update();
  } catch (err) {
    throw new Error(
      `Service worker update check failed: ${err instanceof Error ? err.message : String(err)}`,
    );
  }

  if (registration.installing !== null) {
    await waitForServiceWorkerInstalled(registration.installing);
  }

  return {
    registration,
    hasWaiting: registration.waiting !== null,
  };
}

/**
 * Activates a waiting SW then reloads (same contract as Workbox messageSkipWaiting).
 * Callers must gate payment-pending / checkout via `queuePwaRefreshWhenIdle` or route checks.
 */
export function applyWaitingCustomerServiceWorkerUpdate(
  registration: ServiceWorkerRegistration,
): void {
  registration.waiting?.postMessage({ type: 'SKIP_WAITING' });
  window.location.reload();
}

/** Session guard — at most one silent SW activate+reload per tab session. */
const AUTO_SW_ACTIVATE_SESSION_KEY = 'customer:pwa:auto-sw-activate';

function consumeAutoSwActivateSlot(): boolean {
  try {
    if (window.sessionStorage.getItem(AUTO_SW_ACTIVATE_SESSION_KEY) === '1') {
      return false;
    }
    window.sessionStorage.setItem(AUTO_SW_ACTIVATE_SESSION_KEY, '1');
    return true;
  } catch {
    return false;
  }
}

/**
 * Registers the production service worker.
 * Skipped in Vite dev — `devOptions.enabled` is false, so `/sw.js` is the SPA
 * HTML fallback (MIME text/html) and registration would throw SecurityError.
 */
export function registerCustomerPwaServiceWorker(handlers: PwaUpdateHandlers): void {
  if (!('serviceWorker' in navigator)) {
    return;
  }
  if (import.meta.env.DEV) {
    return;
  }

  // Bypass HTTP cache on SW script fetches (RegistrationOptions.updateViaCache).
  // Workbox forwards the 2nd ctor arg to navigator.serviceWorker.register.
  const workbox = new Workbox('/sw.js', { type: 'classic', updateViaCache: 'none' });

  workbox.addEventListener('waiting', () => {
    let reloadScheduled = false;
    const reloadOnce = (): void => {
      if (reloadScheduled) {
        return;
      }
      reloadScheduled = true;
      window.location.reload();
    };

    const refresh = (): void => {
      workbox.addEventListener('controlling', () => {
        reloadOnce();
      });
      void workbox.messageSkipWaiting();
      window.setTimeout(reloadOnce, 4_000);
    };

    if (isPaymentPending()) {
      queuePwaRefreshWhenIdle(refresh);
      handlers.setUpdateReady(true);
      handlers.setApplyUpdate(() => () => {
        queuePwaRefreshWhenIdle(refresh);
      });
      return;
    }

    // One-shot silent activate when safe: replaces a stuck cache-only navigation
    // SW (F5 → Chrome ERR_FAILED; Ctrl+F5 still worked) without requiring a
    // banner click. Further waiting workers in this tab fall back to the banner.
    if (consumeAutoSwActivateSlot()) {
      refresh();
      return;
    }

    handlers.setUpdateReady(true);
    handlers.setApplyUpdate(() => refresh);
  });

  void workbox.register().catch((err: unknown) => {
    // Missing/invalid SW must not surface as an uncaught promise on boot.
    console.warn('Customer PWA service worker registration failed', err);
  });
}
